import { Sensors, SensorsOff } from "@mui/icons-material";
import IconButton from "@mui/material/IconButton";

import { useAnalytics } from "@/providers";
import {
  PlotPanelConfig,
  ViewportSyncMode,
} from "@/shared/state/visualization/schema";

import { usePlotPanelContext } from "../panelContext";

import { AxisConstraintBadge } from "./AxisConstraintBadge";

/**
 * A button used to synchronize a plot with all other plots.
 */
export function SyncViewportButton({
  config,
}: {
  config: PlotPanelConfig | undefined;
}) {
  const { trackEvent } = useAnalytics();
  const { viewportSyncMode: syncMode, setViewportSyncMode: setSyncMode } =
    usePlotPanelContext();

  const nextSyncMode = useNextSyncMode(config);

  const onClick = () => {
    const next = nextSyncMode(syncMode);
    trackEvent("PlotPanelSyncModeChanged", { syncMode: next });
    setSyncMode(next);
  };

  return (
    <IconButton
      aria-label="sync-chart-viewport"
      onClick={onClick}
      size="small"
      title="Sync viewport to all other charts"
    >
      <AxisConstraintBadge axisConstraint={syncMode}>
        {syncMode !== "" ? (
          <Sensors color="primary" fontSize="small" />
        ) : (
          <SensorsOff color="inherit" fontSize="small" />
        )}
      </AxisConstraintBadge>
    </IconButton>
  );
}

function useNextSyncMode(config: PlotPanelConfig | undefined) {
  const isYLocked = !!config?.yAxis?.bounds?.locked;
  return function nextSyncMode(syncMode: ViewportSyncMode) {
    let next = nextViewportSyncMode[syncMode];
    if (isYLocked && next.includes("y")) {
      next = nextViewportSyncMode[next];
    }
    return next;
  };
}

const nextViewportSyncMode: Record<ViewportSyncMode, ViewportSyncMode> = {
  x: "xy",
  xy: "",
  "": "x",
  y: "x",
};
